import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Swords,
  Bomb,
  Clock,
  Image as ImageIcon,
  Trophy,
  Palette,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

const DURATIONS = [30, 45, 60];
const BLADE_PRESETS = [
  "#a855f7",
  "#EC4899",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#F8FAFC",
];

// Curated demo pack — food/drink products ready for a restaurant / bistro demo.
// Square-cropped 300px images from Unsplash (hotlinkable). The admin can
// remove any of them individually after loading.
const DEMO_PRODUCTS = [
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=300&h=300&fit=crop&q=80", // pizza
  "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=300&fit=crop&q=80", // burger
  "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=300&h=300&fit=crop&q=80", // coffee
  "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=300&h=300&fit=crop&q=80", // pancakes
  "https://images.unsplash.com/photo-1551024506-0bccd828d307?w=300&h=300&fit=crop&q=80", // donut
  "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=300&h=300&fit=crop&q=80", // cocktail
];

/**
 * NinjaEditor — embedded inside AdminGameEditor when game.type === "ninja".
 *
 * Admin configures:
 *   - product images (upload multiple) — optional, falls back to emoji defaults
 *   - bomb image (single) — optional, falls back to default bomb emoji
 *   - duration (30 / 45 / 60s)
 *   - winners_count (1er, 2e, 3e…)
 *   - points per product
 *   - blade color
 *
 * Uploads reuse the /uploads/game-background endpoint.
 */
export default function NinjaEditor({ game, onGameChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadingBomb, setUploadingBomb] = useState(false);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);
  const bombRef = useRef(null);

  useEffect(() => {
    if (!game?.id) return;
    api
      .get(`/games/${game.id}/ninja/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [game?.id]);

  async function uploadProductImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const current = game.ninja_product_images || [];
      onGameChange({ ninja_product_images: [...current, r.data.url] });
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function uploadBombImage(file) {
    if (!file) return;
    setUploadingBomb(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onGameChange({ ninja_bomb_image: r.data.url });
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingBomb(false);
      if (bombRef.current) bombRef.current.value = "";
    }
  }

  function removeProductAt(idx) {
    const current = game.ninja_product_images || [];
    onGameChange({
      ninja_product_images: current.filter((_, i) => i !== idx),
    });
  }

  const products = game.ninja_product_images || [];
  const duration = game.ninja_duration || 45;
  const pointsPerProduct = game.ninja_points_per_product || 10;
  const bladeColor = game.ninja_blade_color || "#a855f7";

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="ninja-editor">
      {/* Top config grid */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t("game_editor.ninja_duration")}
          </Label>
          <div className="mt-1.5 grid grid-cols-3 gap-1.5">
            {DURATIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onGameChange({ ninja_duration: d })}
                className={`h-10 rounded-lg text-xs font-bold border transition-colors ${
                  duration === d
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:bg-muted"
                }`}
                data-testid={`ninja-duration-${d}`}
              >
                {d}s
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Trophy className="h-3 w-3" />
            {t("game_editor.winners_count")}
          </Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={game.winners_count ?? 1}
            onChange={(e) =>
              onGameChange({
                winners_count: Math.max(
                  1,
                  Math.min(100, parseInt(e.target.value) || 1)
                ),
              })
            }
            className="mt-1.5 rounded-xl h-10"
            data-testid="ninja-winners-count"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">
            {t("game_editor.ninja_winners_hint")}
          </p>
        </div>
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Swords className="h-3 w-3" />
            {t("game_editor.ninja_points")}
          </Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={pointsPerProduct}
            onChange={(e) =>
              onGameChange({
                ninja_points_per_product: Math.max(
                  1,
                  Math.min(100, parseInt(e.target.value) || 10)
                ),
              })
            }
            className="mt-1.5 rounded-xl h-10"
            data-testid="ninja-points"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">
            {t("game_editor.ninja_points_hint")}
          </p>
        </div>
      </div>

      {/* Blade color */}
      <div>
        <Label className="text-xs font-medium inline-flex items-center gap-1">
          <Palette className="h-3 w-3" />
          {t("game_editor.ninja_blade_color")}
        </Label>
        <div className="mt-1.5 flex items-center gap-2">
          <input
            type="color"
            value={bladeColor}
            onChange={(e) => onGameChange({ ninja_blade_color: e.target.value })}
            className="h-10 w-14 rounded-lg border border-border cursor-pointer"
            data-testid="ninja-blade-color"
          />
          <Input
            type="text"
            value={bladeColor}
            onChange={(e) => onGameChange({ ninja_blade_color: e.target.value })}
            className="rounded-xl h-10 font-mono text-xs flex-1"
            maxLength={7}
          />
          <div className="flex gap-1">
            {BLADE_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onGameChange({ ninja_blade_color: c })}
                className="h-8 w-8 rounded-md border border-border hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
                title={c}
                data-testid={`ninja-blade-preset-${c}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Product images */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <ImageIcon className="h-3 w-3" />
            {t("game_editor.ninja_product_images")}
          </Label>
          <button
            type="button"
            onClick={() =>
              onGameChange({
                ninja_product_images: [
                  ...(game.ninja_product_images || []),
                  ...DEMO_PRODUCTS.filter(
                    (u) => !(game.ninja_product_images || []).includes(u)
                  ),
                ],
              })
            }
            className="text-[11px] font-semibold inline-flex items-center gap-1 text-violet-600 hover:text-violet-700 dark:text-violet-400"
            data-testid="ninja-load-demo-btn"
          >
            <Sparkles className="h-3 w-3" />
            {t("game_editor.ninja_load_demo")}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug mb-2">
          {t("game_editor.ninja_product_images_hint")}
        </p>

        {products.length > 0 && (
          <div
            className="grid grid-cols-5 gap-2 mb-2"
            data-testid="ninja-products-grid"
          >
            {products.map((url, idx) => (
              <div
                key={url + idx}
                className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted/30"
              >
                <img
                  src={url}
                  alt={`product-${idx}`}
                  className="w-full h-full object-contain"
                />
                <button
                  type="button"
                  onClick={() => removeProductAt(idx)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/95 text-destructive inline-flex items-center justify-center shadow-sm"
                  data-testid={`ninja-product-remove-${idx}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="block rounded-xl border-2 border-dashed border-border hover:border-foreground/40 hover:bg-muted/20 transition-colors cursor-pointer p-5 text-center">
          <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1.5" />
          <div className="text-xs font-medium">
            {uploading
              ? t("rules.generating")
              : t("game_editor.ninja_upload_product")}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            PNG / JPEG · max 5 MB
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => uploadProductImage(e.target.files?.[0])}
            className="hidden"
            data-testid="ninja-product-input"
            disabled={uploading}
          />
        </label>
      </div>

      {/* Bomb image */}
      <div>
        <Label className="text-xs font-medium inline-flex items-center gap-1">
          <Bomb className="h-3 w-3" />
          {t("game_editor.ninja_bomb_image")}
        </Label>
        <p className="text-[11px] text-muted-foreground leading-snug mt-1 mb-2">
          {t("game_editor.ninja_bomb_image_hint")}
        </p>
        {game.ninja_bomb_image ? (
          <div className="relative inline-block rounded-lg overflow-hidden border border-border bg-muted/20">
            <img
              src={game.ninja_bomb_image}
              alt="bomb"
              className="h-24 w-24 object-contain"
              data-testid="ninja-bomb-preview"
            />
            <button
              type="button"
              onClick={() => onGameChange({ ninja_bomb_image: null })}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/95 text-destructive inline-flex items-center justify-center shadow-sm"
              data-testid="ninja-bomb-remove"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <label className="inline-block rounded-xl border-2 border-dashed border-border hover:border-foreground/40 hover:bg-muted/20 transition-colors cursor-pointer px-5 py-3">
            <Upload className="h-4 w-4 inline-block mr-1.5" />
            <span className="text-xs font-medium">
              {uploadingBomb
                ? t("rules.generating")
                : t("game_editor.ninja_upload_bomb")}
            </span>
            <input
              ref={bombRef}
              type="file"
              accept="image/*"
              onChange={(e) => uploadBombImage(e.target.files?.[0])}
              className="hidden"
              data-testid="ninja-bomb-input"
              disabled={uploadingBomb}
            />
          </label>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div
          className="rounded-xl border border-border/60 bg-muted/20 p-3 text-xs space-y-1"
          data-testid="ninja-stats"
        >
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("game_editor.ninja_total_participants")}
            </span>
            <span className="font-semibold">{stats.total_participants}</span>
          </div>
          {stats.top?.length > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("game_editor.ninja_best_score")}
              </span>
              <span className="font-semibold">{stats.top[0].score}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("game_editor.ninja_winners_drawn")}
            </span>
            <span className="font-semibold">{stats.winners_count}</span>
          </div>
        </div>
      )}
    </div>
  );
}
